import { useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';

const emptyPage = () => ({ items: [], skip: 0, count: null, loadBusy: false, error: null, hasMore: true });
const defaultAfter = () => new Date(Date.now() - 72 * 60 * 60 * 1000);
const errorKeys = {
  400: 'invalid-request',
  401: 'unauthorized',
  403: 'forbidden',
  404: 'not-found',
  0: 'network-error',
  '-1': 'network-error',
};

export const useRuleRuns = (ruleId, service, activated, active) => {
  const [after, setAfter] = useState(defaultAfter);
  const [page, setPage] = useState(emptyPage);
  const pageRef = useRef(page);
  const generationRef = useRef(0);
  const mountedRef = useRef(false);

  const publish = next => {
    pageRef.current = next;
    setPage(next);
  };

  const loadPage = async () => {
    const previous = pageRef.current;
    if (!active || !mountedRef.current || previous.loadBusy || !previous.hasMore) return;
    const generation = generationRef.current;
    publish({ ...previous, loadBusy: true, error: null });
    try {
      const result = await service.getRuleRuns(ruleId, {
        count: 100,
        skip: previous.skip,
        after: DateTime.fromJSDate(after).toUTC().toISO({ includeOffset: false }),
      });
      if (!mountedRef.current || generation !== generationRef.current) return;
      const existingIds = new Set(previous.items.map(run => String(run.id)));
      const newItems = result.items.filter(run => {
        const id = String(run.id);
        if (existingIds.has(id)) return false;
        existingIds.add(id);
        return true;
      });
      const skip = previous.skip + result.items.length;
      publish({
        items: [...previous.items, ...newItems],
        skip,
        count: result.count,
        loadBusy: false,
        error: null,
        hasMore: result.items.length > 0 && skip < result.count,
      });
    } catch (error) {
      if (!mountedRef.current || generation !== generationRef.current) return;
      publish({ ...previous, loadBusy: false, error: errorKeys[error?.status] || 'load-error' });
    }
  };

  useEffect(() => {
    mountedRef.current = active;
    generationRef.current += 1;
    publish(emptyPage());
    if (activated && active) void loadPage();
    return () => {
      mountedRef.current = false;
      generationRef.current += 1;
    };
  }, [ruleId, service, after, activated, active]);

  const changeAfter = date => {
    if (!(date instanceof Date) || !Number.isFinite(date.getTime()) || date.getTime() > Date.now()) return;
    if (date.getTime() === after.getTime()) return;
    generationRef.current += 1;
    setAfter(date);
  };

  const loadMore = () => {
    if (active && !pageRef.current.error) void loadPage();
  };

  const cancel = () => {
    mountedRef.current = false;
    generationRef.current += 1;
  };

  const restart = () => {
    cancel();
    setAfter(defaultAfter());
  };

  return { ...page, after, onAfterChange: changeAfter, onLoadMore: loadMore, onRetry: loadPage, cancel, restart };
};